import React, { useState } from 'react';
import { TextField, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BaseDialog, {
  DialogTitle,
  DialogContent,
  DialogActions,
} from '../BaseDialog';
import { Button } from '../../buttons';
import { useClassroomStore } from '../../../hooks/zustand/classrooms';
import { useNewspaperStore } from '../../../hooks/zustand/newspapers';
import * as NewspapersAPI from '../../../api/Newspapers';
import { track, Events } from '../../../utils/analytics';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateNewspaperDialog({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const classrooms = useClassroomStore((state) => state.classrooms);
  const loadClassrooms = useClassroomStore((state) => state.loadClassrooms);
  const addNewspaper = useNewspaperStore((state) => state.addNewspaper);

  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopeType, setScopeType] = useState<'school' | 'classroom' | 'group'>('classroom');
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setStep(0);
    setName('');
    setDescription('');
    setScopeType('classroom');
    setSelectedClassroomIds([]);
    onClose();
  };

  const handleNext = () => {
    if (step === 0) {
      loadClassrooms();
      setStep(1);
    }
  };

  const toggleClassroom = (id: string) => {
    setSelectedClassroomIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleCreate = async () => {
    setIsLoading(true);
    try {
      const schoolId = scopeType === 'school' && classrooms.length > 0
        ? (classrooms[0] as any).school?.id
        : undefined;
      const response = await NewspapersAPI.create({
        name,
        description: description || undefined,
        scope_type: scopeType,
        school_id: schoolId,
        classrooms: scopeType === 'school' ? [] : selectedClassroomIds.map((id) => ({ id })),
      });
      addNewspaper(response.data.data);
      track(Events.CreateNewspaper, {
        scope_type: scopeType,
        has_description: !!description,
      });
      handleClose();
      navigate(`/newspaper/${response.data.data.id}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseDialog
      open={isOpen}
      onClose={handleClose}
      showBackButton={step > 0}
      onBackButton={() => setStep(step - 1)}
    >
      {step === 0 ? (
        <>
          <DialogContent>
            <DialogTitle>Start a newspaper</DialogTitle>
            <TextField
              label="Newspaper name"
              placeholder="e.g. The Lincoln Ledger"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              autoFocus
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button
              label="Next"
              onClick={handleNext}
              disabled={!name.trim()}
            />
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <DialogTitle>Who is this for?</DialogTitle>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Scope</FormLabel>
              <RadioGroup
                value={scopeType}
                onChange={(e) => setScopeType(e.target.value as any)}
              >
                <FormControlLabel value="school" control={<Radio />} label="Our whole school" />
                <FormControlLabel value="classroom" control={<Radio />} label="A classroom" />
                <FormControlLabel value="group" control={<Radio />} label="A small group" />
              </RadioGroup>
            </FormControl>

            {(scopeType === 'classroom' || scopeType === 'group') ? (
              <div>
                <FormLabel component="legend" sx={{ mb: 1, display: 'block' }}>
                  Select classrooms
                </FormLabel>
                {classrooms.map((classroom) => (
                  <FormControlLabel
                    key={classroom.id}
                    control={(
                      <input
                        type="checkbox"
                        checked={selectedClassroomIds.includes(classroom.id)}
                        onChange={() => toggleClassroom(classroom.id)}
                        style={{ marginRight: 8 }}
                      />
                    )}
                    label={classroom.name}
                    sx={{ display: 'block', mb: 1 }}
                  />
                ))}
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              label={isLoading ? 'Creating...' : 'Create newspaper'}
              onClick={handleCreate}
              disabled={isLoading || (scopeType !== 'school' && selectedClassroomIds.length === 0)}
            />
          </DialogActions>
        </>
      )}
    </BaseDialog>
  );
}
